import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';

export interface ChatAuthToken {
  token: string;
  expiresAt: number;
}

@Injectable({
  providedIn: 'root'
})
export class ChatAuthService {

  private readonly AUTH_KEY = 'chat_auth_token';
  private readonly EXPIRY_KEY = 'chat_auth_expiry';
  private readonly EXPIRY_DAYS = 7; // 1 week
  private isAuthenticatedSubject = new BehaviorSubject<boolean>(this.checkAuthentication());
  public isAuthenticated$: Observable<boolean> = this.isAuthenticatedSubject.asObservable();

  constructor() {
    // Check if stored auth is still valid
    this.validateStoredAuth();
  }

  /**
   * Authenticate with password
   */
  authenticate(password: string): boolean {
    const correctPassword = '2026';

    if (password === correctPassword) {
      const expiresAt = this.getExpiryTimestamp();
      localStorage.setItem(this.AUTH_KEY, 'authenticated');
      localStorage.setItem(this.EXPIRY_KEY, expiresAt.toString());
      this.isAuthenticatedSubject.next(true);
      return true;
    }

    return false;
  }

  /**
   * Check if user is authenticated and token is valid
   */
  isAuthenticated(): boolean {
    return this.checkAuthentication();
  }

  /**
   * Get the authentication status observable
   */
  getAuthStatus(): Observable<boolean> {
    return this.isAuthenticated$;
  }

  /**
   * Logout and clear authentication
   */
  logout(): void {
    localStorage.removeItem(this.AUTH_KEY);
    localStorage.removeItem(this.EXPIRY_KEY);
    this.isAuthenticatedSubject.next(false);
  }

  /**
   * Check authentication and validate expiry
   */
  private checkAuthentication(): boolean {
    const token = localStorage.getItem(this.AUTH_KEY);
    const expiry = localStorage.getItem(this.EXPIRY_KEY);

    if (!token || !expiry) {
      return false;
    }

    const expiryTime = parseInt(expiry, 10);
    const now = Date.now();

    if (now > expiryTime) {
      // Token expired
      this.logout();
      return false;
    }

    return true;
  }

  /**
   * Validate stored auth and update subject
   */
  private validateStoredAuth(): void {
    const isValid = this.checkAuthentication();
    this.isAuthenticatedSubject.next(isValid);
  }

  /**
   * Get expiry timestamp (current time + 7 days)
   */
  private getExpiryTimestamp(): number {
    const expiryDate = new Date();
    expiryDate.setDate(expiryDate.getDate() + this.EXPIRY_DAYS);
    return expiryDate.getTime();
  }

  /**
   * Get remaining time in seconds
   */
  getRemainingTime(): number {
    const expiry = localStorage.getItem(this.EXPIRY_KEY);
    if (!expiry) {
      return 0;
    }

    const expiryTime = parseInt(expiry, 10);
    const now = Date.now();
    const remainingMs = expiryTime - now;

    return remainingMs > 0 ? Math.floor(remainingMs / 1000) : 0;
  }
}
